import { NavigateFunction } from 'react-router'

import {
    NotificationCenterAction,
    NotificationCenterActionCategory,
    NotificationCenterCategory,
} from '@/entities/NotificationCenter/NotificationCenter'
import {
    endEventLoggerApiCall,
    startEventLoggerApiCall,
} from '@/entities/NotificationCenter/NotificationCenterService'
import {
    authenticateUserRequest,
    Credentials,
    patchUserPassword,
    postForgotPassword,
} from '@/helpers/api/authenticationApi'
import { getEventLogTrackerData } from '@/helpers/getEventLogTrackerData'
import { trans } from '@/helpers/trans'
import {
    AUTH_AUTHENTICATE_USER_FAILURE,
    AUTH_AUTHENTICATE_USER_REQUEST,
    AUTH_AUTHENTICATE_USER_SUCCESS,
    AUTH_FORGOT_PASSWORD_FAILURE,
    AUTH_FORGOT_PASSWORD_REQUEST,
    AUTH_FORGOT_PASSWORD_SUCCESS,
    AUTH_SET_API_TOKEN,
    AUTH_SET_REFRESH_TOKEN,
    AUTH_SET_TENANT,
    AUTH_SUBMIT_PASSWORD_FAILURE,
    AUTH_SUBMIT_PASSWORD_REQUEST,
    AUTH_SUBMIT_PASSWORD_SUCCESS,
    AUTH_UNSET_TOKENS,
    SET_LOADING,
} from '@/redux/actionTypes'
import { Reducers } from '@/redux/reducers'
import { TypedDispatch } from '@/redux/store'
import { setToast } from '@/redux/toasts/toastsReducer'
import { getProfileOfCurrentUser } from '@/redux/user/userActions'
import { ToastType } from '@/types'

import { AuthenticationAction } from './authenticationReducer'

export const authenticateUser = (credentials: Credentials) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch({ type: AUTH_AUTHENTICATE_USER_REQUEST })
    dispatch({ type: SET_LOADING, loading: true })

    try {
        const response = await authenticateUserRequest(credentials)
        dispatch(
            {
                type: AUTH_AUTHENTICATE_USER_SUCCESS,
                apiToken: response.data.token,
                refreshToken: response.data.refresh_token,
            },
        )
        await dispatch(getProfileOfCurrentUser())
    } catch (_error) {
        dispatch({ type: AUTH_AUTHENTICATE_USER_FAILURE })
    }

    dispatch({ type: SET_LOADING, loading: false })
}

export const setTenant = (tenant: string): AuthenticationAction => ({ type: AUTH_SET_TENANT, tenant })

export const setApiToken = (apiToken: string): AuthenticationAction => ({ type: AUTH_SET_API_TOKEN, apiToken })

export const setRefreshToken = (refreshToken?: string): AuthenticationAction => ({ type: AUTH_SET_REFRESH_TOKEN, refreshToken })

export const unsetTokens = (): AuthenticationAction => ({ type: AUTH_UNSET_TOKENS })

export const forgotUserPassword = (email: string) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch({ type: AUTH_FORGOT_PASSWORD_REQUEST })

    try {
        await postForgotPassword(email)
        dispatch({ type: AUTH_FORGOT_PASSWORD_SUCCESS })
    } catch (_error) {
        dispatch({ type: AUTH_FORGOT_PASSWORD_FAILURE })
    }
}

export const submitUserPasswordPincode = (
    password: string,
    pincode: string,
    loginByQuery: boolean,
    navigate: NavigateFunction,
) => async (dispatch: TypedDispatch, getState: () => Reducers): Promise<void> => {
    dispatch({ type: AUTH_SUBMIT_PASSWORD_REQUEST })

    const eventLogTrackerData = getEventLogTrackerData(await startEventLoggerApiCall(
        NotificationCenterCategory.USER,
        NotificationCenterAction.UPDATED,
        [NotificationCenterActionCategory.UNKNOWN],
    ))

    try {
        const userId = getState().userReducer.currentUser?.id
        if (! userId) throw new Error('No current user')

        await patchUserPassword(userId, password, pincode, eventLogTrackerData)
        // If user was logged in by query, then we need to clear tokens
        if (loginByQuery) {
            dispatch(unsetTokens())
        } else {
            navigate(`/${trans('nav.profile.link')}`)
        }
        dispatch(setToast({ text: trans('pages.changePasswordPincode.changeSuccess'), type: ToastType.pass }))
        dispatch({ type: AUTH_SUBMIT_PASSWORD_SUCCESS })
    } catch (_error) {
        dispatch({ type: AUTH_SUBMIT_PASSWORD_FAILURE })
    } finally {
        await endEventLoggerApiCall(eventLogTrackerData)
    }
}
